import { useState } from "react";
import { useEffect } from "react";
import { connect } from "react-redux";

import { Box, FormControl, FormControlLabel, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { formatISO } from "date-fns";
import { gtag } from "ga-gtag";
import { useSnackbar } from "notistack";

import constants from "../../../assets/constants";
import { createNewMotivation } from "../../../data/omniDataModel";
import * as AssessmentSession from "../../../state/redux/assessmentSession";
import * as PartnerSession from "../../../state/redux/partnerSession";
import * as PatientSession from "../../../state/redux/patientSession";
import * as PatientSessionActions from "../../../state/redux/patientSession";
import * as UserSession from "../../../state/redux/userSession";
import { capitalizeAllWords, OmniStringToDate } from "../../../utils/helpers";
import { deepCopyObject } from "../../../utils/helpers";
import AssessmentWizardDialog from "../../dialogs/assessmentWizardDialog";
import PatientSymptomsDialog from "../../dialogs/demoPatientSymptomsDialog";
import PatientJournalEntryDialog from "../../dialogs/patientJournalEntryDialog";
import PatientMotivationDialog from "../../dialogs/patientMotivationDialog";
import { extractGraphDataFromArray } from "../../graphing/graphUtils";
import NsightPartnerAgendaCalendar from "../../ui/nsightPartnerAgendaCalendar";
import NsightToDoListItem from "../../ui/nsightToDoListItem";
import Checkbox from "../../ui/omniCheckbox";
import Widget from "../../ui/widget";
import WidgetList from "../../ui/widgetList";
import WidgetSkeleton from "../../ui/widgetSkeleton";
import WidgetTitle from "../../ui/widgetTitle";
import NsightInformation from "./nsightInformation";

const statistical = require("statistical-js");

const NsightPartnerAgenda = props => {
  const {
    userODM,
    userToDos,
    loadUserToDosRequest,
    organizationODM,
    updateUserToDosRequest,
    patientChart,
  } = props;

  const [showAll, setShowAll] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const [showWelcomMessages] = useState(false);
  const [showUserSymptomsDialog, setShowUpdateSymptoms] = useState(false);
  const [showAssessmentDialog, setShowAssessmentDialog] = useState(null);
  const [selectedJournalEntry, setSelectedJournalEntry] = useState(null);
  const [selectedMotivation, setSelectedMotivation] = useState(null);
  const [changedToDo, setChangedToDo] = useState(null);
  const [completeToDo, setCompleteToDo] = useState(null);

  const generateFeelingData = theChart => {
    const userInfo = theChart;

    if (userInfo == null) {
      return null;
    }

    for (const [key] of Object.entries(userInfo.goals)) {
      if (key.toLowerCase().includes("feeling")) {
        return extractGraphDataFromArray(userInfo.goals[key].data);
      }
    }
    return null;
  };

  const [feelingData, setFeelingData] = useState(null);

  useEffect(() => {
    setSymptomData(generateSymptomData(patientChart));
    setFeelingData(generateFeelingData(patientChart));
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientChart]);

  const getSingleSymptomEntry = (symptom, dateString) => {
    const theReturn = { name: symptom.name, value: null };
    try {
      const theDate = OmniStringToDate(dateString);

      for (let index = 0; index < symptom.graphLabels.length; index++) {
        const labelDate = OmniStringToDate(symptom.graphLabels[index]);
        if (labelDate - theDate > 0) {
          theReturn.value = symptom.graphDataComplete[index - 1];
          break;
        }
      }
    } catch (error) {
      theReturn.value = 0;
      return theReturn;
    }
    if (theReturn.value == null) {
      theReturn.value = symptom.graphDataComplete[symptom.graphDataComplete.length - 1];
    }
    return theReturn;
  };

  const getSymptomValues = dateString => {
    const userInfo = symptomData;
    if (userInfo == null) {
      return [];
    }
    const theReturn = [];
    try {
      for (let index = 0; index < symptomData.length; index++) {
        theReturn.push(getSingleSymptomEntry(symptomData[index], dateString));
      }
    } catch (error) {
      return theReturn;
    }
    return theReturn;
  };

  const [localToDos, setLocalToDos] = useState(null);
  const [filteredToDos, setFilteredToDos] = useState(null);

  const handleEditButtonClick = toDo => {
    setChangedToDo(toDo);

    if (toDo.toDoType === "takeAnotherAssessment") {
      setShowAssessmentDialog("medication");
    } else if (toDo.toDoType === "phq9Assessment") {
      setShowAssessmentDialog(constants.assessmentIDs.phq9);
    } else if (toDo.toDoType === "trackSymptoms") {
      setShowUpdateSymptoms(true);
    } else if (toDo.toDoType === "medication") {
      setShowAssessmentDialog(constants.assessmentIDs.omniMedicationReminderDemo);
    } else if (toDo.toDoType === "journal") {
      const today = new Date();
      setSelectedJournalEntry({
        id: null,
        date: today.toISOString(),
        note: "",
        feeling: `${
          constants.feelingLabels[feelingData.values[feelingData.values.length - 1]]
        } Day`,
      });
    } else if (toDo.toDoType === "motivations") {
      setSelectedMotivation(createNewMotivation);
    }
    gtag("event", "partner_todo", {
      todo_type: toDo.toDoType,
    });
  };

  useEffect(() => {
    try {
      let result = deepCopyObject(localToDos);
      if (!showAll) {
        result = result.filter(({ isCompleted }) => isCompleted === false);
      }
      setFilteredToDos(result);
    } catch (error) {
      console.log(error);
    }
  }, [showAll, localToDos, setFilteredToDos]);

  const handleDialogClose = buttonClicked => {
    if (buttonClicked === "ok") {
      setCompleteToDo(true);
    } else {
      setCompleteToDo(false);
    }
  };

  useEffect(() => {
    if (showWelcomMessages) {
      setTimeout(() => {
        enqueueSnackbar("Thank you!", {
          variant: "success",
        });
      }, 2000);
    }
  }, [showWelcomMessages, enqueueSnackbar]);

  const generateSymptomData = () => {
    const userInfo = patientChart;
    if (userInfo == null) {
      return null;
    }

    const singleSymptomsData = [];
    for (const [key] of Object.entries(userInfo.symptoms)) {
      let graphData = extractGraphDataFromArray(userInfo.symptoms[key].data);

      let linearInput = [];
      for (let x = 0; x < graphData.values.length; ++x) {
        linearInput.push([x, graphData.values[x]]);
      }
      //this takes an array [x,y] values
      const linearOutput = statistical.methods.linearRegression(linearInput);

      const average = statistical.methods.mean(graphData.values);

      singleSymptomsData.push({
        name: capitalizeAllWords(key.replace(/([A-Z])/g, " $1").trim()),
        averageSeverity: Math.round(average),
        regressionValue: linearOutput,
        graphDataComplete: graphData.values,
        graphLabels: graphData.labels,
        graphIndex: 0,
        symptomId: key,
      });
    }

    singleSymptomsData.sort((a, b) => b.averageSeverity - a.averageSeverity);

    //set the Fake editing data

    return singleSymptomsData;
  };

  const [symptomData, setSymptomData] = useState(generateSymptomData());

  useEffect(() => {
    setSymptomData(generateSymptomData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientChart]);

  useEffect(() => {
    if (userToDos) {
      let length = userToDos.length;
      setTitleText(`${length} ${length === 1 ? "Reminder" : "Reminders"}`);
    }
  }, [userToDos]);

  useEffect(() => {
    if (userToDos == null && organizationODM != null && userODM != null) {
      //find!ak THIS IS REPLACED WHEN WE MOVE TODO TO THE DB/db
      loadUserToDosRequest();
    } else if (
      userToDos != null &&
      organizationODM != null &&
      userODM != null &&
      localToDos == null
    ) {
      setLocalToDos(userToDos);
    }
  }, [organizationODM, userODM, userToDos, localToDos, loadUserToDosRequest]);

  const [titleText, setTitleText] = useState("");
  const [title, setTitle] = useState("Loading...");

  useEffect(() => {
    if (userODM === null) {
      setTitleText("");
      setTitle("Loading...");
    } else {
      setTitle("This Week");
    }
  }, [userODM]);

  useEffect(() => {
    if (changedToDo && localToDos && completeToDo) {
      let myToDo = changedToDo;
      myToDo.isCompleted = true;
      myToDo.completeDate = formatISO(new Date());

      let letsChangeTheseToDos = deepCopyObject(localToDos);
      letsChangeTheseToDos[letsChangeTheseToDos.findIndex(toDo => toDo.key === myToDo.key)] =
        myToDo;
      setLocalToDos(letsChangeTheseToDos);
      updateUserToDosRequest(letsChangeTheseToDos);
      setChangedToDo(null);
      setCompleteToDo(false);
    }
  }, [localToDos, changedToDo, completeToDo, updateUserToDosRequest, setLocalToDos]);

  const theme = useTheme();

  return (
    <Widget>
      <WidgetTitle title={title} text={titleText} loading={userODM == null} />
      <Box
        sx={{
          margin: theme.spacing(1),
          marginRight: theme.spacing(2),
          marginLeft: theme.spacing(2),
          paddingBottom: theme.spacing(1),
          marginBottom: `-${theme.spacing(1)}`,
          borderBottom: `1px solid ${alpha(theme.palette.commonground.contrastText, 0.25)}`,
        }}
      >
        <NsightPartnerAgendaCalendar userToDos={userToDos} />
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginRight: theme.spacing(0),
          marginLeft: theme.spacing(2),
          marginTop: `-${theme.spacing(1)}`,
          marginBottom: `-${theme.spacing(2)}`,
        }}
      >
        <Typography variant="overline" sx={{ marginTop: theme.spacing(3) }}>
          {localToDos != null && localToDos.filter(toDo => toDo.isCompleted === false).length > 0
            ? localToDos.filter(toDo => toDo.isCompleted === false).length + " more to go"
            : "All Done!"}
        </Typography>
        <Tooltip title={"Show All Tasks"} placement="left">
          <FormControl
            component="fieldset"
            sx={{ marginRight: "14px", marginTop: theme.spacing(2) }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={showAll}
                  onChange={() => setShowAll(!showAll)}
                  name="showAll"
                  color="secondary"
                />
              }
              labelPlacement="start"
              label={<Typography variant="overline">Show Completed</Typography>}
              sx={{ userSelect: "none", webkitUserSelect: "none", marginTop: "3px" }}
            />
          </FormControl>
        </Tooltip>
      </Box>

      {filteredToDos == null && <WidgetSkeleton />}
      {filteredToDos != null && filteredToDos.length > 0 && (
        <WidgetList>
          {filteredToDos.map(toDo => {
            return (
              <NsightToDoListItem
                key={toDo.key}
                toDoODM={toDo}
                onClickHandler={handleEditButtonClick}
              />
            );
          })}
        </WidgetList>
      )}
      {filteredToDos != null && filteredToDos.length === 0 && <NsightInformation />}
      {selectedJournalEntry != null && (
        <PatientJournalEntryDialog
          open={selectedJournalEntry != null}
          dialogTitle={`${selectedJournalEntry.id === null ? "New Entry" : "Update Journal"}`}
          dialogMessage={selectedJournalEntry.feeling}
          journalEntry={selectedJournalEntry}
          symptomData={getSymptomValues(selectedJournalEntry.date)}
          onAccept={journalEntry => {
            props.updatePatientJournalEntryRequest(journalEntry);
            if (changedToDo) {
              handleDialogClose("ok");
            }
            setSelectedJournalEntry(null);
          }}
          onClose={() => {
            setSelectedJournalEntry(null);
          }}
        />
      )}
      {selectedMotivation != null && (
        <PatientMotivationDialog
          open={selectedMotivation != null}
          dialogTitle={`${selectedMotivation._id == null ? "Add" : "Edit"} Motivation`}
          motivationODM={selectedMotivation}
          textLabel={"Case Motivation"}
          onAccept={motivation => {
            // the returned note is a deepCopiedObject,
            props.updateMotivationRequest(motivation);
            setSelectedMotivation(null);
            if (changedToDo) {
              handleDialogClose("ok");
            }
          }}
          onClose={() => {
            setSelectedMotivation(null);
          }}
        />
      )}
      {showUserSymptomsDialog && (
        <PatientSymptomsDialog
          open={symptomData != null}
          symptomData={symptomData}
          onAccept={updatedSymptoms => {
            //update the PatientChart with the new data
            let today = new Date();
            today = today.toISOString().substring(0, 10);
            let newSymptoms = deepCopyObject(patientChart.symptoms);
            for (let index = 0; index < updatedSymptoms.length; ++index) {
              let theNewEntry = {
                date: today,
                value: updatedSymptoms[index].newReportedValue,
                note: "",
              };
              newSymptoms[updatedSymptoms[index].symptomId].data.push(theNewEntry);
            }

            props.updatePatientSymptomsRequest(newSymptoms);
            if (changedToDo) {
              handleDialogClose("ok");
            }
            setShowUpdateSymptoms(false);
          }}
          onClose={() => {
            setShowUpdateSymptoms(false);
          }}
        />
      )}
      {showAssessmentDialog != null && (
        <AssessmentWizardDialog
          open={showAssessmentDialog != null}
          lockAssessmentOmniType={showAssessmentDialog}
          autoStartAssessment={true}
          lockSubjectODM={userODM}
          onAccept={() => {
            if (changedToDo) {
              handleDialogClose("ok");
            }
            setShowAssessmentDialog(null);
          }}
          onClose={() => {
            if (changedToDo) {
              handleDialogClose("cancel");
              // completeToDo(changedToDo);
            }
            setShowAssessmentDialog(null);
          }}
        />
      )}
    </Widget>
  );
};

const mapStateToProps = state => ({
  patientAssessments: state.patientSession.patientAssessments,
  partnerPatients: state.partnerSession.partnerPatients,
  partnerAssessments: state.partnerSession.partnerAssessments,
  partnerInvites: state.partnerSession.partnerInvites,
  userODM: state.userSession.userODM,
  userKPIs: state.userSession.userKPIs,
  organizationODM: state.userSession.organizationODM,
  userToDos: state.userSession.userToDos,
  awsUserGroup: state.userSession.awsGroup,

  patientODM: state.patientSession.patientODM,
  patientChart: state.patientSession.patientChart,
});

const mapDispatchToProps = {
  updatePartnerRequest: PartnerSession.updatePartnerRequest,
  updatePatientRequest: PatientSession.updatePatientRequest,
  updateAssessmentRequest: AssessmentSession.updateAssessmentRequest,
  updateUserToDosRequest: UserSession.updateUserToDosRequest,
  loadUserToDosRequest: UserSession.loadUserToDosRequest,
  updateUserKPIRequest: UserSession.updateUserKPIRequest,
  updatePatientSymptomsRequest: PatientSessionActions.updatePatientSymptomsRequest,
  updatePatientJournalEntryRequest: PatientSessionActions.updatePatientJournalEntryRequest,
  updateMotivationRequest: PatientSession.updateMotivationRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(NsightPartnerAgenda);
